import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function RiskDisclaimer() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-20 pb-12 px-4" data-testid="disclaimer-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/">
          <Button variant="ghost" className="mb-6 text-gray-400 hover:text-white">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
          </Button>
        </Link>
        
        <Card className="bg-[#14120f] border-[#1c1814]">
          <CardHeader className="border-b border-[#1c1814]">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-[#D4AF37]" />
              <div>
                <CardTitle className="text-2xl text-white">Risk Disclaimer</CardTitle>
                <p className="text-gray-400 text-sm">Last updated: January 2025</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6 prose prose-invert max-w-none">
            <div className="space-y-6 text-gray-300">
              {/* Important Notice Box */}
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
                <h2 className="text-xl font-bold text-red-400 mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-6 w-6" />
                  IMPORTANT NOTICE
                </h2>
                <p className="text-red-200 font-semibold">
                  This platform is for EDUCATIONAL PURPOSES ONLY. Nothing on this platform constitutes financial advice, investment advice, trading advice, or any other sort of advice. You should not treat any of the platform's content as such.
                </p>
              </div>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">Not Financial Advice</h2>
                <p>
                  Nouveau Riche Legacy and its affiliates are NOT licensed financial advisors, investment advisors, or broker-dealers. The content provided on this platform is for informational and educational purposes only.
                </p>
                <p className="mt-2">
                  We do not provide personalized investment advice or recommendations regarding the purchase or sale of any securities, investments, or financial products.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">Trading Risks</h2>
                <p className="font-semibold text-[#D4AF37]">
                  Trading stocks, cryptocurrencies, forex, and other financial instruments involves substantial risk of loss and is not suitable for every investor.
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>You can lose some or ALL of your invested capital</li>
                  <li>Past performance is NOT indicative of future results</li>
                  <li>Cryptocurrency markets are highly volatile and unregulated</li>
                  <li>Forex trading carries significant leverage risk</li>
                  <li>Options and derivatives can result in losses exceeding initial investment</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">Paper Trading vs. Real Trading</h2>
                <p>
                  Our platform offers paper trading (simulated trading with fake money). Please understand that:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Paper trading results DO NOT reflect real market conditions</li>
                  <li>Success in paper trading does NOT guarantee success in real trading</li>
                  <li>Real trading involves emotional factors not present in simulation</li>
                  <li>Slippage, fees, and market impact are not fully simulated</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">Third-Party Integrations</h2>
                <p>
                  When you connect to third-party brokers like Alpaca for live trading:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>You are using REAL MONEY with REAL RISK</li>
                  <li>Nouveau Riche Legacy is NOT responsible for any trading losses</li>
                  <li>Your brokerage account is governed by the broker's terms</li>
                  <li>We do not guarantee execution quality or prices</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">AI Financial Advisor</h2>
                <p>
                  Our AI-powered financial advisor feature is:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>For educational and informational purposes ONLY</li>
                  <li>NOT a substitute for professional financial advice</li>
                  <li>Generated by artificial intelligence which may produce inaccurate information</li>
                  <li>Not personalized to your specific financial situation</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">Copy Trading Risks</h2>
                <p>
                  Copy trading and following other traders carries additional risks:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Past performance of other traders does NOT guarantee future results</li>
                  <li>You may lose money even when copying successful traders</li>
                  <li>Other traders' strategies may not be suitable for your risk tolerance</li>
                  <li>Execution delays may result in different outcomes</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">Consult Professionals</h2>
                <p className="font-semibold text-emerald-400">
                  Before making any financial decisions, we strongly recommend that you:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Consult with a licensed financial advisor</li>
                  <li>Speak with a certified public accountant (CPA) regarding tax implications</li>
                  <li>Consult with a licensed attorney for legal matters</li>
                  <li>Only invest money you can afford to lose</li>
                  <li>Conduct your own research before making any investment decision</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">No Guarantees</h2>
                <p>
                  Nouveau Riche Legacy makes NO guarantees regarding:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Accuracy of market data or information provided</li>
                  <li>Performance of any trading strategy or educational content</li>
                  <li>Outcomes of any investment decisions based on our content</li>
                  <li>Availability or reliability of third-party services</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">Limitation of Liability</h2>
                <p>
                  By using this platform, you acknowledge and agree that Nouveau Riche Legacy and its affiliates, officers, directors, employees, and agents shall NOT be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising from:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Your use of the platform or reliance on any information provided</li>
                  <li>Any trading losses or investment losses</li>
                  <li>Actions taken based on educational content</li>
                  <li>Technical failures or service interruptions</li>
                </ul>
              </section>

              {/* Acknowledgment Box */}
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-6">
                <h2 className="text-xl font-bold text-[#D4AF37] mb-3">Your Acknowledgment</h2>
                <p className="text-gray-200">
                  By using the Nouveau Riche Legacy platform, you acknowledge that you have read, understood, and agree to this Risk Disclaimer. You accept full responsibility for your own investment decisions and acknowledge that trading and investing involves substantial risk of loss.
                </p>
              </div>

              <section>
                <h2 className="text-xl font-bold text-white mb-3">Contact Us</h2>
                <p>
                  If you have questions about this Risk Disclaimer, please contact us at: legal@nouveaurichelegacy.com
                </p>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
